import { useCallback, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useField } from "formik";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  ErrorMessage,
  Flex,
  LoadingIndicator,
  ModalV2,
  SearchOption,
  toast,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import {
  FormCheckbox,
  FormRadioButton,
  FormRadioGroup,
  FormTextInput,
  SubmitButton,
  useFormState,
} from "@jsq/ds/forms";
import { globalEnv } from "~/global-env";
import {
  useEntityFromProspectLazyQuery,
  useGetComplianceCaseByProspectLazyQuery,
  useGetPositionContextLazyQuery,
} from "~/graphql/supergraphHooks";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { ExitWarningModal } from "../../../../Common/ExitWarningModal";
import { extractIdFromPathSegment } from "../../../../utils";
import { useInvestorWizard } from "../../context";
import { ArenaTypeahead } from "../ArenaTypeahead";
import { EntityTypeahead } from "../EntityTypeahead";
import * as styles from "../styles_vanilla.css";
import { extractLookupId, isLookupOutOfSync } from "../utils";
import type { EntityOption } from "../EntityTypeahead";
import type { CaseInfoFormValues, CreationMethod } from "../schema";

const extractIdOrToast = (idOrUrl: string, segment: string, label: string): number | null => {
  const id = extractIdFromPathSegment(idOrUrl, segment);
  if (!id) {
    toast.error(`${label} could not be deciphered`);
  }
  return id;
};

export const CaseInfoFields = () => {
  const { values, setFieldValue } = useFormState<CaseInfoFormValues>();
  const history = useHistory();
  const { setCreationMethod } = useInvestorWizard();
  const arenaRef = useRef<TypeaheadInputComponent>(null);
  const entityRef = useRef<TypeaheadInputComponent>(null);
  const previousCreationMethod = useRef<string>(values.creationMethod);
  const [existingCaseId, setExistingCaseId] = useState<number | null>(null);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [lookedUpLookupId, setLookedUpLookupId] = useState(() =>
    extractLookupId(values.creationMethod, values),
  );

  const [, clientMeta] = useField<string>("clientId");
  const [, entityMeta] = useField<string>("entityId");

  const [investmentEntityFromProspect] = useEntityFromProspectLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getComplianceCaseByProspect] = useGetComplianceCaseByProspectLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getPositionContext] = useGetPositionContextLazyQuery({
    fetchPolicy: "no-cache",
  });

  const isLookupMode =
    values.creationMethod === "prospect" || values.creationMethod === "position";

  const clearEntityFields = useCallback(() => {
    void setFieldValue("entityId", undefined);
    void setFieldValue("entityName", undefined);
    void setFieldValue("programName", undefined);
  }, [setFieldValue]);

  const populateClientAndEntity = useCallback(
    (fields: {
      clientId: string;
      clientName?: string | null;
      entityId: string;
      entityName?: string | null;
      program?: string | null;
    }) => {
      arenaRef.current?.clearInputValue();
      entityRef.current?.clearInputValue();
      void setFieldValue("clientId", fields.clientId);
      void setFieldValue("clientName", fields.clientName ?? "");
      void setFieldValue("entityId", fields.entityId);
      void setFieldValue("entityName", fields.entityName ?? "");
      void setFieldValue("programName", fields.program ?? "");
    },
    [setFieldValue],
  );

  const handleCreationMethodChange = useCallback(
    (method: string) => {
      if (method === previousCreationMethod.current) return;
      previousCreationMethod.current = method;
      setCreationMethod(method as CreationMethod);
      void setFieldValue("clientId", undefined);
      void setFieldValue("clientName", undefined);
      void setFieldValue("prospectIdOrSubscriptionUrl", "");
      void setFieldValue("positionId", "");
      setExistingCaseId(null);
      setLookedUpLookupId("");
      clearEntityFields();
      arenaRef.current?.clearInputValue();
      entityRef.current?.clearInputValue();
    },
    [setFieldValue, clearEntityFields, setCreationMethod],
  );

  const handleSelectClient = useCallback(
    (opt: SearchOption) => {
      void setFieldValue("clientId", opt.id);
      void setFieldValue("clientName", opt.label);
      clearEntityFields();
      entityRef.current?.clearInputValue();
    },
    [setFieldValue, clearEntityFields],
  );

  const handleSelectEntity = useCallback(
    (opt: EntityOption) => {
      void setFieldValue("entityId", opt.id);
      void setFieldValue("entityName", opt.label);
      void setFieldValue("programName", opt.program ?? "");
    },
    [setFieldValue],
  );

  const handleClearClient = useCallback(() => {
    void setFieldValue("clientId", undefined);
    void setFieldValue("clientName", undefined);
    clearEntityFields();
    entityRef.current?.clearInputValue();
  }, [setFieldValue, clearEntityFields]);

  const handleCancel = useCallback(() => setShowExitWarning(true), []);

  const handleConfirmExit = useCallback(
    () => history.push(globalEnv.complianceRoute),
    [history],
  );

  const withLookupLoading = useCallback(async (task: () => Promise<void>) => {
    setIsLookupLoading(true);
    try {
      await task();
    } finally {
      setIsLookupLoading(false);
    }
  }, []);

  const handleProspectLookup = useCallback(async () => {
    const prospectId = extractIdOrToast(
      values.prospectIdOrSubscriptionUrl,
      "prospect",
      "Prospect ID",
    );
    if (!prospectId) return;

    await withLookupLoading(async () => {
      const { data: prospectData, error: prospectErr } = await investmentEntityFromProspect({
        variables: { prospectId: prospectId.toString(), includeDeleted: false },
      });
      const entity = prospectData?.investmentEntityFromProspect;
      const arena = entity?.arena;
      if (prospectErr || !entity || !arena) {
        toast.error(
          `Failed to get client and entity information using prospect id ${prospectId}`,
        );
        return;
      }

      const { data, error: caseErr } = await getComplianceCaseByProspect({
        variables: { arenaId: toDbId(arena.id), prospectId },
      });
      if (caseErr) {
        const isUnauthorized = caseErr.graphQLErrors?.some((e) =>
          e.message?.includes("not authorized for one or more workspaces"),
        );
        toast.error(
          isUnauthorized
            ? "You are not authorized to access this workspace"
            : `Failed to check for existing compliance cases for prospect ${prospectId}`,
        );
        return;
      }

      const linkedCaseId = data?.complianceCaseByProspectId?.id;
      if (linkedCaseId) {
        setExistingCaseId(linkedCaseId);
        return;
      }

      setLookedUpLookupId(prospectId.toString());
      populateClientAndEntity({
        clientId: arena.id,
        clientName: arena.name,
        entityId: entity.id,
        entityName: entity.name,
        program: entity.complianceSettings?.complianceProgram,
      });
    });
  }, [
    investmentEntityFromProspect,
    getComplianceCaseByProspect,
    populateClientAndEntity,
    withLookupLoading,
    values.prospectIdOrSubscriptionUrl,
  ]);

  const handlePositionLookup = useCallback(async () => {
    const positionId = extractIdOrToast(values.positionId, "position", "Position ID");
    if (!positionId) return;

    await withLookupLoading(async () => {
      const positionContextResult = await getPositionContext({
        variables: {
          positionId: toGlobalId("PositionObjectType", positionId.toString()),
        },
      });

      if (positionContextResult.error) {
        toast.error(`Failed to look up position ${positionId}`);
        return;
      }

      const positionContext = positionContextResult.data?.positionForComplianceUser;

      const linkedCaseId = positionContext?.onboardingCase?.id ?? null;
      if (linkedCaseId) {
        setExistingCaseId(linkedCaseId);
        return;
      }

      const arena = positionContext?.arena;
      const entity = positionContext?.investmentEntity;

      if (!arena?.id || !entity?.id) {
        toast.error(`No position found with id ${positionId} in any arena you have access to.`);
        return;
      }

      setLookedUpLookupId(positionId.toString());
      populateClientAndEntity({
        clientId: arena.id,
        clientName: arena.name,
        entityId: entity.id,
        entityName: entity.name,
        program: entity.complianceSettings?.complianceProgram,
      });
    });
  }, [getPositionContext, populateClientAndEntity, withLookupLoading, values.positionId]);

  const handleLookup = useCallback(
    () =>
      values.creationMethod === "position" ? handlePositionLookup() : handleProspectLookup(),
    [values.creationMethod, handlePositionLookup, handleProspectLookup],
  );

  const handleLookupKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter" || isLookupLoading) {
        return;
      }

      event.preventDefault();
      void handleLookup();
    },
    [handleLookup, isLookupLoading],
  );

  const handleViewExistingCase = useCallback(() => {
    if (!existingCaseId) return;
    setExistingCaseId(null);
    history.push(`${globalEnv.complianceRoute}/case/${existingCaseId}`);
  }, [history, existingCaseId]);

  const lookupOutOfSync = isLookupOutOfSync(values.creationMethod, values, lookedUpLookupId);

  const handleContinueClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (lookupOutOfSync) {
        event.preventDefault();
        toast.error(
          "The ID was changed after lookup. Click Update to refresh client and entity information.",
        );
      }
    },
    [lookupOutOfSync],
  );

  return (
    <>
      <Card title="Case information">
        <FormRadioGroup
          className={styles.radioGroupField}
          name="creationMethod"
          label="Which information will you use to create this case?"
          onValueChanged={handleCreationMethodChange}
        >
          <FormRadioButton value="prospect" data-testid="creation-method-prospect">
            Prospect ID or subscription URL
          </FormRadioButton>
          <FormRadioButton value="client" data-testid="creation-method-client">
            Client or entity name
          </FormRadioButton>
          <FormRadioButton value="position" data-testid="creation-method-position">
            Position ID or URL
          </FormRadioButton>
        </FormRadioGroup>

        {isLookupMode && (
          <div className={styles.clientEntityRow}>
            <Flex gap={8} alignItems="flex-end">
              <div className={styles.lookupInputWrapper}>
                {values.creationMethod === "position" ? (
                  <FormTextInput
                    label="Position ID or URL"
                    name="positionId"
                    data-testid="position-id"
                    isFullWidth
                    onKeyDown={handleLookupKeyDown}
                  />
                ) : (
                  <FormTextInput
                    label="Prospect ID or Subscription URL"
                    name="prospectIdOrSubscriptionUrl"
                    data-testid="prospect-id-or-subscription-url"
                    isFullWidth
                    onKeyDown={handleLookupKeyDown}
                  />
                )}
              </div>
              <Button
                variant="neutral"
                onClick={() => void handleLookup()}
                disabled={isLookupLoading}
                className={styles.lookupButton}
              >
                {isLookupLoading ? <LoadingIndicator size="small" /> : "Update"}
              </Button>
            </Flex>
          </div>
        )}

        <Flex className={styles.clientEntityRow} gap={20} flexDirection="row">
          <Flex flexDirection="column">
            <ArenaTypeahead
              key={`arena-${values.creationMethod}`}
              disabled={isLookupMode}
              onSelect={handleSelectClient}
              onClear={handleClearClient}
              inputRef={arenaRef}
              placeholder={isLookupMode || values.clientId ? values.clientName : ""}
              initialSearchValue={values.clientName ?? ""}
            />
            {clientMeta.touched && clientMeta.error && (
              <ErrorMessage>{clientMeta.error}</ErrorMessage>
            )}
          </Flex>
          <Flex flexDirection="column">
            <EntityTypeahead
              key={`entity-${values.creationMethod}`}
              disabled={isLookupMode}
              arenaId={values.clientId || undefined}
              onSelect={handleSelectEntity}
              onClear={clearEntityFields}
              inputRef={entityRef}
              placeholder={isLookupMode || values.entityId ? values.entityName : ""}
              initialSearchValue={values.entityName ?? ""}
            />
            {entityMeta.touched && entityMeta.error && (
              <ErrorMessage>{entityMeta.error}</ErrorMessage>
            )}
          </Flex>
        </Flex>

        <FormCheckbox name="assignToMyself" label="Assign case to myself" />
      </Card>
      <Flex justifyContent="flex-end" gap={8}>
        <Button variant="neutral" onClick={handleCancel}>
          Cancel
        </Button>
        <SubmitButton
          disabled={!values.clientId || !values.entityId}
          onClick={handleContinueClick}
        >
          Continue
        </SubmitButton>
      </Flex>

      <ModalV2
        show={!!existingCaseId}
        closeButton
        closeOnClickOutside={false}
        escapeToClose={false}
        onHide={() => setExistingCaseId(null)}
        title="Case Already Exists"
        size="small"
        data-testid="case-exists-modal"
      >
        <ModalV2.Body>
          {values.creationMethod === "position"
            ? "A compliance case already exists for this position."
            : "A compliance case already exists for this prospect."}
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap="md">
            <Button variant="primary" onClick={handleViewExistingCase}>
              View case
            </Button>
          </Flex>
        </ModalV2.Footer>
      </ModalV2>

      <ExitWarningModal
        show={showExitWarning}
        onCancel={() => setShowExitWarning(false)}
        onConfirm={handleConfirmExit}
      />
    </>
  );
};
